import { useContext, useMemo } from 'react';
import { ReactReduxContext } from 'react-redux';
import { useAppSelector } from '../../../redux/hooks';
import { useUserState } from '../../../query/UserProvider';
import { extractJobsFromUserState } from '../../../utils/user-state/extract-jobs-from-user-state';
import { resolveJobUrlTitle } from '../../../utils/routing/job-url-input';
import { pickRecentSearchChips, pickRecentlyViewedJobs, type RecentSearchChip } from '../menu/MenuContinueSection';
import { resolveValueSheetVariant, type ValueSheetVariant } from './value-sheet-variant';

export interface ValueSheetLedger {
  variant: ValueSheetVariant;
  /** Up to 3, newest first, same source and cap as the menu's "pick up where you left off". */
  recentSearches: RecentSearchChip[];
  lastViewedTitle: string | null;
}

function useLastViewedTitle(): string | null {
  const { data: userStateData } = useUserState();
  return useMemo(() => {
    const viewedJobs = extractJobsFromUserState(userStateData ?? {}).viewedJobs;
    const [mostRecent] = pickRecentlyViewedJobs(viewedJobs, 1);
    if (!mostRecent) return null;
    return resolveJobUrlTitle(mostRecent) || mostRecent.title || null;
  }, [userStateData]);
}

/** Variant for chrome where the Redux store (recent searches) is mounted: mirrors
 * MenuContinueSection's own ContinueWithSearches/ContinueViewedOnly split, for the same reason:
 * useAppSelector must never be called where there's no Redux Provider above it. */
function useLedgerWithSearches(locale: string): ValueSheetLedger {
  const entries = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  const recentSearches = useMemo(() => pickRecentSearchChips(entries, locale as 'en' | 'fi' | 'sv'), [entries, locale]);
  const lastViewedTitle = useLastViewedTitle();
  return {
    variant: resolveValueSheetVariant(recentSearches.length > 0, Boolean(lastViewedTitle)),
    recentSearches,
    lastViewedTitle,
  };
}

function useLedgerViewedOnly(): ValueSheetLedger {
  const lastViewedTitle = useLastViewedTitle();
  return {
    variant: resolveValueSheetVariant(false, Boolean(lastViewedTitle)),
    recentSearches: [],
    lastViewedTitle,
  };
}

/** Chooses which ledger hook to run once per mount (whether a Redux store exists above this
 * component doesn't change during its lifetime), same guard MenuContinueSection uses. */
export function useValueSheetLedger(locale: string): ValueSheetLedger {
  const hasReduxStore = useContext(ReactReduxContext) != null;
  // eslint-disable-next-line react-hooks/rules-of-hooks -- stable for the component's lifetime, see comment above
  return hasReduxStore ? useLedgerWithSearches(locale) : useLedgerViewedOnly();
}
